import { useEffect, useState } from "react";
import axios from "axios";
import { Search, Plus, Trash2, X, Copy, Check, TrendingUp, Users, DollarSign, Activity } from "lucide-react";
import { apiUrl } from "../../../utils/config";

const API = apiUrl;

const fmt = (v) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format(Number(v || 0));

const statusCls = {
  Active:    "bg-green-100 text-green-700 border-green-200",
  Inactive:  "bg-gray-100 text-gray-500 border-gray-200",
  Suspended: "bg-red-100 text-red-600 border-red-200",
};

const emptyForm = { name: "", email: "", code: "", commission_rate: 10 };

const AffiliatesPage = () => {
  const [affiliates, setAffiliates]   = useState([]);
  const [stats, setStats]             = useState(null);
  const [loading, setLoading]         = useState(true);
  const [search, setSearch]           = useState("");
  const [filterStatus, setFilterStatus] = useState("All");
  const [drawerOpen, setDrawerOpen]   = useState(false);
  const [form, setForm]               = useState(emptyForm);
  const [saving, setSaving]           = useState(false);
  const [deleteId, setDeleteId]       = useState(null);
  const [copiedId, setCopiedId]       = useState(null);
  const [formError, setFormError]     = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [listRes, statsRes] = await Promise.all([
        axios.get(`${API}/affiliates`, { params: { search: search || undefined, status: filterStatus !== "All" ? filterStatus : undefined } }),
        axios.get(`${API}/affiliates/stats`),
      ]);
      setAffiliates(listRes.data.list || []);
      setStats(statsRes.data.stats || null);
    } catch {
      setAffiliates([]);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, [search, filterStatus]);

  const openDrawer = () => { setForm(emptyForm); setFormError(""); setDrawerOpen(true); };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { setFormError("Name and email are required."); return; }
    setSaving(true);
    setFormError("");
    try {
      const { data } = await axios.post(`${API}/affiliates`, form);
      if (data.status) { setDrawerOpen(false); load(); }
      else setFormError(data.error || "Failed to create affiliate.");
    } catch { setFormError("Something went wrong."); }
    setSaving(false);
  };

  const updateStatus = async (id, status) => {
    await axios.put(`${API}/affiliates/${id}`, { status }).catch(() => {});
    setAffiliates((prev) => prev.map((a) => a.id === id ? { ...a, status } : a));
  };

  const handleDelete = async (id) => {
    await axios.delete(`${API}/affiliates/${id}`).catch(() => {});
    setAffiliates((prev) => prev.filter((a) => a.id !== id));
    setDeleteId(null);
    load();
  };

  const copyCode = (code, id) => {
    navigator.clipboard.writeText(code).then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  const statCards = stats ? [
    { label: "Total Affiliates", value: stats.total,                      icon: Users,      color: "bg-blue-500"   },
    { label: "Active",           value: stats.active,                     icon: Activity,   color: "bg-green-500"  },
    { label: "Total Referrals",  value: stats.total_referrals,            icon: TrendingUp, color: "bg-orange-500" },
    { label: "Total Earned",     value: fmt(stats.total_earned),          icon: DollarSign, color: "bg-purple-500" },
  ] : [];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Affiliates</h1>
          <p className="text-gray-500 text-sm">Manage referral partners and track commissions.</p>
        </div>
        <button
          onClick={openDrawer}
          className="bg-orange-500 hover:bg-orange-600 text-white px-5 py-2.5 rounded-xl font-bold shadow-lg shadow-orange-200 flex items-center gap-2 transition active:scale-95"
        >
          <Plus size={20} /> Add Affiliate
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
          {statCards.map((s) => (
            <div key={s.label} className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm flex items-center gap-4">
              <div className={`p-3 rounded-xl ${s.color}`}>
                <s.icon size={20} className="text-white" />
              </div>
              <div>
                <p className="text-xl font-extrabold text-gray-900">{s.value}</p>
                <p className="text-xs text-gray-500">{s.label}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pending payout banner */}
      {stats && Number(stats.pending_payout) > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-center justify-between">
          <div>
            <p className="font-bold text-amber-800">Pending Payouts</p>
            <p className="text-sm text-amber-600">{fmt(stats.pending_payout)} owed to affiliates</p>
          </div>
          <span className="bg-amber-500 text-white text-xs font-black px-3 py-1.5 rounded-full">Action Required</span>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="p-5 border-b border-gray-100 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <h3 className="font-bold text-lg text-gray-800">All Affiliates</h3>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex bg-gray-100 rounded-xl p-1 gap-1">
              {["All", "Active", "Inactive", "Suspended"].map((s) => (
                <button
                  key={s}
                  onClick={() => setFilterStatus(s)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition ${filterStatus === s ? "bg-white shadow text-orange-600" : "text-gray-500 hover:text-gray-700"}`}
                >
                  {s}
                </button>
              ))}
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-2.5 text-gray-400" size={16} />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search affiliates..."
                className="pl-9 pr-4 py-2 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-orange-500 w-48"
              />
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          {loading ? (
            <div className="p-10 text-center text-gray-400 text-sm">Loading affiliates...</div>
          ) : (
            <table className="w-full text-left whitespace-nowrap">
              <thead className="bg-gray-50 text-gray-500 text-xs uppercase font-bold tracking-wider">
                <tr>
                  <th className="p-5">Affiliate</th>
                  <th className="p-5">Code</th>
                  <th className="p-5">Commission</th>
                  <th className="p-5">Referrals</th>
                  <th className="p-5">Earned</th>
                  <th className="p-5">Pending</th>
                  <th className="p-5">Status</th>
                  <th className="p-5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {affiliates.length === 0 && (
                  <tr><td colSpan={8} className="p-10 text-center text-gray-400">No affiliates found.</td></tr>
                )}
                {affiliates.map((a) => (
                  <tr key={a.id} className="hover:bg-gray-50 transition">
                    <td className="p-5">
                      <p className="font-bold text-gray-900">{a.name}</p>
                      <p className="text-xs text-gray-400">{a.email}</p>
                    </td>
                    <td className="p-5">
                      <div className="flex items-center gap-2">
                        <code className="text-xs bg-gray-100 px-2 py-1 rounded-lg font-mono text-gray-700">{a.code}</code>
                        <button onClick={() => copyCode(a.code, a.id)} className="text-gray-400 hover:text-orange-500 transition">
                          {copiedId === a.id ? <Check size={14} className="text-green-500" /> : <Copy size={14} />}
                        </button>
                      </div>
                    </td>
                    <td className="p-5 font-semibold text-gray-700">{a.commission_rate}%</td>
                    <td className="p-5 font-bold text-gray-900">{a.total_referrals}</td>
                    <td className="p-5 font-bold text-green-600">{fmt(a.total_earned)}</td>
                    <td className="p-5 font-bold text-amber-600">{fmt(a.pending_payout)}</td>
                    <td className="p-5">
                      <select
                        value={a.status}
                        onChange={(e) => updateStatus(a.id, e.target.value)}
                        className={`text-xs font-bold px-2.5 py-1.5 rounded-full border cursor-pointer outline-none ${statusCls[a.status] || statusCls.Inactive}`}
                      >
                        <option>Active</option>
                        <option>Inactive</option>
                        <option>Suspended</option>
                      </select>
                    </td>
                    <td className="p-5 text-right">
                      <button onClick={() => setDeleteId(a.id)} className="p-2 hover:bg-red-50 rounded-lg text-red-400 transition">
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Add Affiliate Drawer */}
      {drawerOpen && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-gray-900/50 backdrop-blur-sm" onClick={() => setDrawerOpen(false)} />
          <div className="relative w-full max-w-md bg-white h-full shadow-2xl flex flex-col">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <h2 className="text-lg font-black text-gray-900">Add Affiliate</h2>
              <button onClick={() => setDrawerOpen(false)} className="p-2 hover:bg-gray-100 rounded-full transition">
                <X size={20} className="text-gray-500" />
              </button>
            </div>
            <form onSubmit={handleSave} className="flex-1 overflow-y-auto p-5 space-y-4">
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 block">Full Name *</label>
                <input
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Jane Smith"
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-orange-200"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 block">Email *</label>
                <input
                  required
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="jane@example.com"
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-orange-200"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 block">Referral Code</label>
                <input
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                  placeholder="Auto-generated if blank"
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-orange-200 font-mono"
                />
                <p className="text-xs text-gray-400 mt-1">Leave blank to auto-generate from name.</p>
              </div>
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 block">Commission Rate (%)</label>
                <input
                  type="number"
                  min="1"
                  max="100"
                  step="0.5"
                  value={form.commission_rate}
                  onChange={(e) => setForm({ ...form, commission_rate: e.target.value })}
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-orange-200"
                />
              </div>
              {formError && (
                <p className="text-sm text-red-500 bg-red-50 px-3 py-2 rounded-xl">{formError}</p>
              )}
            </form>
            <div className="p-5 border-t border-gray-100 flex gap-3">
              <button type="button" onClick={() => setDrawerOpen(false)} className="flex-1 border border-gray-200 text-gray-600 font-bold py-3 rounded-xl hover:bg-gray-50 transition">
                Cancel
              </button>
              <button onClick={handleSave} disabled={saving} className="flex-1 bg-orange-500 hover:bg-orange-600 text-white font-bold py-3 rounded-xl shadow-lg shadow-orange-200 transition disabled:opacity-60">
                {saving ? "Saving..." : "Add Affiliate"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-sm p-6 text-center">
            <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Trash2 size={26} className="text-red-500" />
            </div>
            <h2 className="text-lg font-extrabold text-gray-900">Delete Affiliate?</h2>
            <p className="text-sm text-gray-500 mt-2">This will remove the affiliate and their referral code permanently.</p>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setDeleteId(null)} className="flex-1 px-5 py-2.5 rounded-xl border border-gray-200 text-gray-600 font-bold hover:bg-gray-50 transition">Cancel</button>
              <button onClick={() => handleDelete(deleteId)} className="flex-1 px-5 py-2.5 rounded-xl bg-red-500 hover:bg-red-600 text-white font-bold transition active:scale-95">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AffiliatesPage;
